import React, { useState, useEffect, useRef } from 'react';
import {
    X, Camera, Users, Crown, Shield, UserPlus, UserMinus, LogOut,
    Edit3, Check, ChevronRight, Image, Link as LinkIcon, FileText,
    MoreVertical, Search
} from 'lucide-react';
import { ref, get, onValue } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../../../integrations/firebase';
import { GroupService } from '../../services/GroupService';
import { useChat } from '../../hooks/useChat';
import { useToast } from '../../../../shared/ui/Toast';

const GroupInfoPanel = ({ activeChat, messages, onClose, allUsers }) => {
    const { currentUser } = useChat();
    const { showToast } = useToast();
    const userId = currentUser?.uid || currentUser?.id;

    const [groupData, setGroupData] = useState(null);
    const [members, setMembers] = useState([]);
    const [myRole, setMyRole] = useState(null);
    const [editingName, setEditingName] = useState(false);
    const [editingDesc, setEditingDesc] = useState(false);
    const [newName, setNewName] = useState('');
    const [newDesc, setNewDesc] = useState('');
    const [showAddMember, setShowAddMember] = useState(false);
    const [memberSearch, setMemberSearch] = useState('');
    const [activeTab, setActiveTab] = useState('members');
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const photoInputRef = useRef(null);

    const chatId = activeChat?.chatId;

    // Load group data in real-time.
    //
    // ⚠️ This used to subscribe to `conversations/{chatId}` — the WHOLE node, i.e. every
    // message the group has ever sent — to show six small fields, and kept that live for
    // as long as the panel was open. RTDB bills egress, so each field is listened to on
    // its own instead.
    useEffect(() => {
        if (!chatId) return;
        setGroupData(null);
        const FIELDS = ['name', 'description', 'photo', 'ownerId', 'admins', 'members'];
        const unsubs = FIELDS.map(field => onValue(
            ref(db, `conversations/${chatId}/${field}`),
            (snap) => {
                const val = snap.exists() ? snap.val() : null;
                setGroupData(prev => ({ ...(prev || {}), [field]: val }));
                if (field === 'name') setNewName(val || '');
                if (field === 'description') setNewDesc(val || '');
            },
            // Removed from the group while the panel is open: access ends, so does the panel's data.
            () => setGroupData(null)
        ));
        return () => unsubs.forEach(u => u());
    }, [chatId]);

    // Resolve members to user objects
    useEffect(() => {
        if (!groupData?.members) { setMembers([]); return; }

        const memberIds = Object.keys(groupData.members);
        const resolved = memberIds.map(uid => {
            const user = allUsers?.find(u => u.id === uid || u.uid === uid);
            let role = 'member';
            if (groupData.ownerId === uid) role = 'owner';
            else if (groupData.admins?.[uid]) role = 'admin';

            return {
                id: uid,
                name: user?.name || user?.displayName || 'Unknown User',
                email: user?.email || '',
                photo: user?.photo || user?.photoURL || null,
                role,
                status: user?.status || 'offline'
            };
        });

        // Sort: owner first, then admins, then members
        resolved.sort((a, b) => {
            const order = { owner: 0, admin: 1, member: 2 };
            return (order[a.role] || 2) - (order[b.role] || 2);
        });

        setMembers(resolved);
    }, [groupData, allUsers]);

    // Get my role
    useEffect(() => {
        if (!groupData || !userId) return;
        if (groupData.ownerId === userId) setMyRole('owner');
        else if (groupData.admins?.[userId]) setMyRole('admin');
        else if (groupData.members?.[userId]) setMyRole('member');
        else setMyRole(null);
    }, [groupData, userId]);

    // --- Actions ---
    const handleSaveName = async () => {
        if (!newName.trim()) return;
        const result = await GroupService.updateGroupInfo(chatId, { name: newName.trim() }, userId);
        if (result.success) {
            showToast('Group name updated', 'success');
            setEditingName(false);
        } else {
            showToast(result.error || 'Failed to update name', 'error');
        }
    };

    const handleSaveDesc = async () => {
        const result = await GroupService.updateGroupInfo(chatId, { description: newDesc.trim() }, userId);
        if (result.success) {
            showToast('Description updated', 'success');
            setEditingDesc(false);
        } else {
            showToast(result.error || 'Failed to update description', 'error');
        }
    };

    const handlePhotoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploadingPhoto(true);
        try {
            const fileRef = storageRef(storage, `group_photos/${chatId}/${Date.now()}_${file.name}`);
            await uploadBytes(fileRef, file);
            const url = await getDownloadURL(fileRef);
            await GroupService.updateGroupInfo(chatId, { photo: url }, userId);
            showToast('Group photo updated', 'success');
        } catch (err) {
            showToast('Failed to upload photo', 'error');
        } finally {
            setUploadingPhoto(false);
        }
    };

    const handleAddMember = async (memberId) => {
        const result = await GroupService.addMembers(chatId, [memberId], userId);
        if (result.success) {
            showToast('Member added', 'success');
            setShowAddMember(false);
            setMemberSearch('');
        } else {
            showToast(result.error || 'Failed to add member', 'error');
        }
    };

    const handleRemoveMember = async (memberId) => {
        const result = await GroupService.removeMember(chatId, memberId, userId);
        if (result.success) {
            showToast('Member removed', 'success');
        } else {
            showToast(result.error || 'Failed to remove member', 'error');
        }
    };

    const handlePromoteAdmin = async (memberId) => {
        try {
            const updates = {};
            updates[`conversations/${chatId}/admins/${memberId}`] = true;
            const { update: fbUpdate } = await import('firebase/database');
            await fbUpdate(ref(db), updates);
            showToast('Promoted to admin', 'success');
        } catch (err) {
            showToast('Failed to promote', 'error');
        }
    };

    const handleDemoteAdmin = async (memberId) => {
        try {
            const { update: fbUpdate } = await import('firebase/database');
            await fbUpdate(ref(db), { [`conversations/${chatId}/admins/${memberId}`]: null });
            showToast('Removed admin role', 'success');
        } catch (err) {
            showToast('Failed to demote', 'error');
        }
    };

    const handleLeaveGroup = async () => {
        const result = await GroupService.leaveGroup(chatId, userId);
        if (result.success) {
            showToast('Left the group', 'success');
            onClose();
        } else {
            showToast(result.error || 'Failed to leave group', 'error');
        }
    };

    // Non-member users for "Add Member"
    const availableUsers = (allUsers || []).filter(u => {
        const uid = u.id || u.uid;
        if (!uid || !groupData?.members) return false;
        return !groupData.members[uid] && (u.name || u.email);
    }).filter(u => {
        if (!memberSearch) return true;
        const q = memberSearch.toLowerCase();
        return (u.name?.toLowerCase().includes(q)) || (u.email?.toLowerCase().includes(q));
    });

    // Media/docs from messages
    const mediaMessages = (messages || []).filter(m => m.type === 'image' || m.type === 'video');
    const docMessages = (messages || []).filter(m => m.type === 'file');

    const isOwnerOrAdmin = myRole === 'owner' || myRole === 'admin';

    const getRoleIcon = (role) => {
        if (role === 'owner') return <Crown size={14} className="text-amber-500" />;
        if (role === 'admin') return <Shield size={14} className="text-indigo-500" />;
        return null;
    };

    const getRoleBadge = (role) => {
        if (role === 'owner') return <span className="text-[10px] font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full">Owner</span>;
        if (role === 'admin') return <span className="text-[10px] font-bold text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded-full">Admin</span>;
        return null;
    };

    if (!groupData) {
        return (
            <div className="w-80 h-full border-l border-slate-200 bg-white flex items-center justify-center">
                <div className="animate-pulse text-slate-400">Loading...</div>
            </div>
        );
    }

    return (
        <div className="w-80 h-full border-l border-slate-200 bg-white flex flex-col shrink-0 animate-in slide-in-from-right-10 duration-300">
            {/* Header */}
            <div className="h-14 px-4 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900">
                <span className="font-bold text-white text-sm">Group Info</span>
                <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-white rounded-full hover:bg-white/10 transition-colors">
                    <X size={18} />
                </button>
            </div>

            <div className="flex-1 overflow-y-auto custom-scrollbar">
                {/* Group Photo & Name */}
                <div className="flex flex-col items-center p-6 border-b border-slate-100">
                    {/* Photo with edit overlay */}
                    <div className="relative group mb-4">
                        <div className="w-24 h-24 rounded-2xl overflow-hidden bg-gradient-to-br from-indigo-100 to-violet-100 shadow-md">
                            {groupData.photo ? (
                                <img src={groupData.photo} alt={groupData.name} className="w-full h-full object-cover" />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-3xl font-bold text-indigo-500">
                                    {(groupData.name || 'G').charAt(0).toUpperCase()}
                                </div>
                            )}
                        </div>
                        {isOwnerOrAdmin && (
                            <button
                                onClick={() => photoInputRef.current?.click()}
                                className="absolute inset-0 bg-black/40 rounded-2xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                            >
                                <Camera size={20} className="text-white" />
                            </button>
                        )}
                        <input
                            ref={photoInputRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handlePhotoUpload}
                        />
                        {uploadingPhoto && (
                            <div className="absolute inset-0 bg-black/50 rounded-2xl flex items-center justify-center">
                                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            </div>
                        )}
                    </div>

                    {/* Group Name */}
                    {editingName ? (
                        <div className="flex items-center gap-2 w-full max-w-[200px]">
                            <input
                                value={newName}
                                onChange={(e) => setNewName(e.target.value)}
                                className="flex-1 text-center text-lg font-bold bg-slate-50 border border-indigo-200 rounded-lg px-2 py-1 focus:ring-2 focus:ring-indigo-200 focus:outline-none"
                                autoFocus
                                onKeyDown={(e) => e.key === 'Enter' && handleSaveName()}
                            />
                            <button onClick={handleSaveName} className="p-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
                                <Check size={14} />
                            </button>
                        </div>
                    ) : (
                        <div className="flex items-center gap-2">
                            <h2 className="text-lg font-bold text-slate-800">{groupData.name}</h2>
                            {isOwnerOrAdmin && (
                                <button onClick={() => setEditingName(true)} className="p-1 text-slate-400 hover:text-indigo-600 rounded-full hover:bg-slate-100">
                                    <Edit3 size={14} />
                                </button>
                            )}
                        </div>
                    )}

                    <p className="text-xs text-slate-400 mt-1">
                        Group · {members.length} member{members.length !== 1 ? 's' : ''}
                    </p>
                </div>

                {/* Description */}
                <div className="p-4 border-b border-slate-100">
                    <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Description</p>
                        {isOwnerOrAdmin && !editingDesc && (
                            <button onClick={() => setEditingDesc(true)} className="p-1 text-slate-400 hover:text-indigo-600 rounded-full hover:bg-slate-100">
                                <Edit3 size={12} />
                            </button>
                        )}
                    </div>
                    {editingDesc ? (
                        <div className="flex flex-col gap-2">
                            <textarea
                                value={newDesc}
                                onChange={(e) => setNewDesc(e.target.value)}
                                className="w-full text-sm bg-slate-50 border border-indigo-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-200 focus:outline-none resize-none"
                                rows={3}
                                autoFocus
                            />
                            <div className="flex justify-end gap-2">
                                <button onClick={() => setEditingDesc(false)} className="text-xs text-slate-500 hover:text-slate-700 px-3 py-1.5 rounded-lg hover:bg-slate-100">Cancel</button>
                                <button onClick={handleSaveDesc} className="text-xs text-white bg-indigo-600 px-3 py-1.5 rounded-lg hover:bg-indigo-700">Save</button>
                            </div>
                        </div>
                    ) : (
                        <p className="text-sm text-slate-600 leading-relaxed">
                            {groupData.description || 'No description set.'}
                        </p>
                    )}
                </div>

                {/* Tabs: Members / Media / Docs */}
                <div className="flex border-b border-slate-100">
                    {[
                        { key: 'members', label: 'Members', icon: <Users size={14} /> },
                        { key: 'media', label: 'Media', icon: <Image size={14} /> },
                        { key: 'docs', label: 'Docs', icon: <FileText size={14} /> },
                    ].map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key)}
                            className={`flex-1 py-3 text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors ${
                                activeTab === tab.key
                                    ? 'text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50/50'
                                    : 'text-slate-400 hover:text-slate-600'
                            }`}
                        >
                            {tab.icon} {tab.label}
                        </button>
                    ))}
                </div>

                {/* TAB CONTENT */}
                <div className="flex-1">
                    {/* MEMBERS TAB */}
                    {activeTab === 'members' && (
                        <div>
                            {/* Add Member Button */}
                            {isOwnerOrAdmin && (
                                <button
                                    onClick={() => setShowAddMember(!showAddMember)}
                                    className="w-full flex items-center gap-3 p-3 text-indigo-600 hover:bg-indigo-50/50 transition-colors border-b border-slate-50"
                                >
                                    <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                                        <UserPlus size={18} className="text-indigo-600" />
                                    </div>
                                    <span className="text-sm font-semibold">Add Members</span>
                                </button>
                            )}

                            {/* Add Member Search */}
                            {showAddMember && (
                                <div className="p-3 bg-slate-50 border-b border-slate-100 animate-in slide-in-from-top-2">
                                    <div className="relative mb-2">
                                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                        <input
                                            value={memberSearch}
                                            onChange={(e) => setMemberSearch(e.target.value)}
                                            placeholder="Search people..."
                                            className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-200 focus:outline-none"
                                            autoFocus
                                        />
                                    </div>
                                    <div className="max-h-40 overflow-y-auto custom-scrollbar">
                                        {availableUsers.length === 0 ? (
                                            <p className="text-xs text-slate-400 text-center py-3">No users to add</p>
                                        ) : (
                                            availableUsers.slice(0, 10).map(user => (
                                                <button
                                                    key={user.id || user.uid}
                                                    onClick={() => handleAddMember(user.id || user.uid)}
                                                    className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-white transition-colors"
                                                >
                                                    <img
                                                        src={user.photo || `https://ui-avatars.com/api/?name=${user.name}&size=32`}
                                                        className="w-8 h-8 rounded-full object-cover"
                                                        alt=""
                                                    />
                                                    <div className="text-left flex-1 min-w-0">
                                                        <p className="text-sm font-medium text-slate-700 truncate">{user.name}</p>
                                                        <p className="text-[10px] text-slate-400 truncate">{user.email}</p>
                                                    </div>
                                                    <UserPlus size={14} className="text-indigo-500" />
                                                </button>
                                            ))
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Member List */}
                            <div className="divide-y divide-slate-50">
                                {members.map(member => (
                                    <div key={member.id} className="flex items-center gap-3 p-3 hover:bg-slate-50/50 transition-colors group">
                                        <div className="relative">
                                            <img
                                                src={member.photo || `https://ui-avatars.com/api/?name=${member.name}&size=40`}
                                                className="w-10 h-10 rounded-full object-cover"
                                                alt=""
                                            />
                                            {member.status === 'online' && (
                                                <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-emerald-400 rounded-full border-2 border-white" />
                                            )}
                                        </div>

                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-1.5">
                                                <p className="text-sm font-medium text-slate-700 truncate">
                                                    {member.name}
                                                    {member.id === userId && <span className="text-slate-400 font-normal"> (You)</span>}
                                                </p>
                                                {getRoleIcon(member.role)}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <p className="text-[10px] text-slate-400 truncate">{member.email}</p>
                                                {getRoleBadge(member.role)}
                                            </div>
                                        </div>

                                        {/* Actions for owner/admin on other members */}
                                        {isOwnerOrAdmin && member.id !== userId && member.role !== 'owner' && (
                                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                {myRole === 'owner' && (
                                                    <>
                                                        {member.role === 'admin' ? (
                                                            <button
                                                                onClick={() => handleDemoteAdmin(member.id)}
                                                                className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                                                                title="Remove Admin"
                                                            >
                                                                <Shield size={14} />
                                                            </button>
                                                        ) : (
                                                            <button
                                                                onClick={() => handlePromoteAdmin(member.id)}
                                                                className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                                                                title="Make Admin"
                                                            >
                                                                <Shield size={14} />
                                                            </button>
                                                        )}
                                                    </>
                                                )}
                                                <button
                                                    onClick={() => handleRemoveMember(member.id)}
                                                    className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                    title="Remove Member"
                                                >
                                                    <UserMinus size={14} />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* MEDIA TAB */}
                    {activeTab === 'media' && (
                        <div className="p-3">
                            {mediaMessages.length === 0 ? (
                                <p className="text-center text-slate-400 text-sm py-8">No media shared</p>
                            ) : (
                                <div className="grid grid-cols-3 gap-1.5">
                                    {mediaMessages.map((msg, i) => (
                                        <a key={i} href={msg.fileUrl} target="_blank" rel="noreferrer" className="aspect-square rounded-lg overflow-hidden bg-slate-100 hover:opacity-80 transition-opacity">
                                            <img src={msg.fileUrl} className="w-full h-full object-cover" alt="" />
                                        </a>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* DOCS TAB */}
                    {activeTab === 'docs' && (
                        <div className="p-3">
                            {docMessages.length === 0 ? (
                                <p className="text-center text-slate-400 text-sm py-8">No documents shared</p>
                            ) : (
                                <div className="space-y-2">
                                    {docMessages.map((msg, i) => (
                                        <a
                                            key={i}
                                            href={msg.fileUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-slate-50 transition-colors"
                                        >
                                            <div className="p-2 bg-indigo-100 rounded-lg">
                                                <FileText size={16} className="text-indigo-600" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-slate-700 truncate">{msg.fileName || 'Document'}</p>
                                                <p className="text-[10px] text-slate-400">{msg.fileSize || ''}</p>
                                            </div>
                                        </a>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Leave Group */}
                {myRole && myRole !== 'owner' && (
                    <div className="p-4 border-t border-slate-100">
                        <button
                            onClick={handleLeaveGroup}
                            className="w-full flex items-center justify-center gap-2 py-2.5 text-red-500 hover:bg-red-50 rounded-xl transition-colors text-sm font-semibold"
                        >
                            <LogOut size={16} />
                            Leave Group
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default GroupInfoPanel;
